import React from 'react';
import './Select.css';

export interface SelectOption {
  id?: string | number;
  value?: string | number;
  label?: string;
  name?: string;
  kindName?: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  id?: string;
  name?: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  variant?: 'default' | 'modal';
  label?: string;
  hint?: string;
  labelClassName?: string;
  hintClassName?: string;
}

/**
 * Reusable Select component with label and hint support
 */
const Select: React.FC<SelectProps> = ({
  id,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'None',
  disabled = false,
  className = '',
  variant = 'default',
  label,
  hint,
  labelClassName = '',
  hintClassName = '',
  ...rest
}) => {
  const baseClass = `common-select common-select--${variant}`;
  const selectClassName = className ? `${baseClass} ${className}` : baseClass;
  
  const labelClass = variant === 'modal' 
    ? `common-select__label ${labelClassName}`.trim()
    : `common-select__label common-select__label--default ${labelClassName}`.trim();
  
  const hintClass = variant === 'modal'
    ? `common-select__hint ${hintClassName}`.trim()
    : `common-select__hint common-select__hint--default ${hintClassName}`.trim();

  const selectElement = (
    <select
      id={id}
      name={name}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className={selectClassName}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => {
        // Support both {id, label, value} and {value, label} formats
        const optionValue = option.value !== undefined ? option.value : option.id;
        const optionLabel = option.label || option.name || option.kindName || String(optionValue);
        
        return (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        );
      })}
    </select>
  );

  // If label or hint is provided, wrap in a container
  if (label || hint) {
    return (
      <div className="common-select__wrapper">
        {label && (
          <label htmlFor={id} className={labelClass}>
            {label}
          </label>
        )}
        {selectElement}
        {hint && (
          <small className={hintClass}>
            {hint}
          </small>
        )}
      </div>
    );
  }

  // Otherwise, return just the select element
  return selectElement;
};

export default Select;

